export const BRL_PER_USD = 5.5;

/**
 * Quanto por cento se cobra a mais do que a Twilio cobra pela linha. E so o
 * padrao: quem administra o SaaS muda isso nas configuracoes.
 */
export const LINE_MARGIN_PERCENT = 10;

/** Quanto por cento se cobra a mais do que a Twilio cobra por mensagem. */
export const MESSAGE_MARGIN_PERCENT = 100;

/**
 * Os pacotes de mensagem que acompanham a mensalidade. O primeiro e so a
 * linha: quem nao quer mensagem no pacote paga o numero e recarrega depois.
 */
export const MESSAGE_PACKS = [0, 500, 1000, 3000, 5000] as const;

export const LOW_BALANCE_MESSAGES = 50;

export const BILLING_NOTICE_DAYS = 7;

const MS_PER_DAY = 86_400_000;

export function toCents(value: number): number {
  return Math.round(value * 100) / 100;
}

export interface LinePricing {
  marginPercent: number;
  usdRate: number;
}

export const DEFAULT_LINE_PRICING: LinePricing = {
  marginPercent: LINE_MARGIN_PERCENT,
  usdRate: BRL_PER_USD,
};

/**
 * A mensalidade que o salao paga pela linha: o preco da Twilio em real, mais
 * a margem. Margem e percentual - 10 cobra dez por cento a mais, nao dez
 * vezes.
 */
export function monthlyLinePrice(
  providerUsd: number,
  pricing: LinePricing = DEFAULT_LINE_PRICING,
): number {
  return toCents(providerUsd * pricing.usdRate * (1 + pricing.marginPercent / 100));
}

export function toMillis(value: number): number {
  return Math.round(value * 10_000) / 10_000;
}

export function messageCreditCost(providerUsd: number): number {
  return toMillis(providerUsd * BRL_PER_USD * (1 + MESSAGE_MARGIN_PERCENT / 100));
}

export function balanceCoversMessage(balance: number, cost: number): boolean {
  return balance >= cost;
}

export function messagesLeft(balance: number, cost: number): number {
  return cost <= 0 ? 0 : Math.floor(balance / cost);
}

export function nextBillingPeriod(start: Date): { start: Date; end: Date } {
  const end = new Date(start);
  const diaContratado = start.getUTCDate();

  end.setUTCMonth(end.getUTCMonth() + 1);
  if (end.getUTCDate() !== diaContratado) end.setUTCDate(0);

  return { start: new Date(start), end };
}

export function lineIsPaidOn(paidThrough: Date | null | undefined, moment: Date): boolean {
  return !!paidThrough && paidThrough.getTime() > moment.getTime();
}

export function daysUntil(paidThrough: Date | null | undefined, moment: Date): number | null {
  if (!paidThrough) return null;

  return Math.ceil((paidThrough.getTime() - moment.getTime()) / MS_PER_DAY);
}

export const MESSAGE_PRICING_MODES = ['AUTO', 'FIXED'] as const;

export type MessagePricingMode = (typeof MESSAGE_PRICING_MODES)[number];

export interface MessagePricing {
  mode: MessagePricingMode;
  price: number;
  marginPercent: number;
  usdRate: number;
}

export const DEFAULT_MESSAGE_PRICING: MessagePricing = {
  mode: 'AUTO',
  price: 0,
  marginPercent: MESSAGE_MARGIN_PERCENT,
  usdRate: BRL_PER_USD,
};

export function messagePriceFrom(
  providerUsd: number | null,
  pricing: MessagePricing = DEFAULT_MESSAGE_PRICING,
): number | null {
  if (pricing.mode === 'FIXED') return toMillis(pricing.price);
  if (providerUsd === null) return null;

  return toMillis(providerUsd * pricing.usdRate * (1 + pricing.marginPercent / 100));
}

export interface MessagePriceTable {
  outbound_sms_prices?: {
    prices?: { number_type?: string; current_price?: string; base_price?: string }[];
  }[];
}

export function highestMessagePrice(table: MessagePriceTable): number | null {
  const valores = (table.outbound_sms_prices ?? [])
    .flatMap((operadora) => operadora.prices ?? [])
    .map((preco) => Number(preco.current_price ?? preco.base_price))
    .filter((valor) => Number.isFinite(valor));

  return valores.length === 0 ? null : Math.max(...valores);
}

export const ASAAS_ENVIRONMENTS = ['SANDBOX', 'PRODUCTION'] as const;

export type AsaasEnvironment = (typeof ASAAS_ENVIRONMENTS)[number];

export const ASAAS_BASE_URL: Record<AsaasEnvironment, string> = {
  SANDBOX: 'https://api-sandbox.asaas.com/v3',
  PRODUCTION: 'https://api.asaas.com/v3',
};

export const MIN_CREDIT_TOPUP = 50;

export interface PromotionalPlanPrice {
  monthlyPrice: number;
  introPrice: number | null;
  introMonths: number;
}

/** Valor do plano em um mes da contratacao, contando o primeiro mes como 1. */
export function planPriceAtMonth(plan: PromotionalPlanPrice, month: number): number {
  const introPrice = plan.introPrice;
  const promocaoAtiva = introPrice !== null && plan.introMonths > 0 && month <= plan.introMonths;

  return promocaoAtiva ? introPrice : plan.monthlyPrice;
}

/** Total previsivel de uma contratacao para a quantidade de meses informada. */
export function planPriceForMonths(plan: PromotionalPlanPrice, months: number): number {
  return toCents(
    Array.from({ length: Math.max(0, months) }, (_, index) =>
      planPriceAtMonth(plan, index + 1),
    ).reduce((total, price) => total + price, 0),
  );
}

/** O que o assinante paga pelas mensagens do pacote, no preco unitario de hoje. */
export function messagePackPrice(messages: number, messagePrice: number | null): number {
  return messagePrice === null ? 0 : toCents(messages * messagePrice);
}

/** Cabecalho em que o Asaas devolve o segredo configurado no webhook. */
export const ASAAS_WEBHOOK_TOKEN_HEADER = 'asaas-access-token';
